/**
 * 抽查任务操作：逐单判定、提交结论、建任务
 *
 * 操作人由后端从登录态取，前端不传 —— 抽查结论会导致地址永久下线并处罚 DSP，
 * 操作人是处罚依据链的一环。
 */
import {useCallback, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {message} from 'antd';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {useTranslation} from 'react-i18next';
import {SampleJudgeResult, SpotTaskConclusion} from '../types/ExemptSpotCheckType';
import {
    CONCLUDE_SPOT_TASK,
    CREATE_SPOT_TASK,
    ConcludeSpotTaskResponse,
    CreateSpotTaskInput,
    CreateSpotTaskResponse,
    JUDGE_SPOT_SAMPLE,
    JudgeSpotSampleResponse,
} from '../graphql/mutations/spotTaskMutations';

interface UseSpotTaskActionsResult {
    loading: boolean;
    judgeSample: (sampleId: number, result: SampleJudgeResult, remark?: string) => Promise<boolean>;
    concludeTask: (
        taskId: number,
        conclusion: SpotTaskConclusion,
        remark: string,
    ) => Promise<boolean>;
    createTaskFromMonitor: (input: CreateSpotTaskInput) => Promise<boolean>;
}

export const useSpotTaskActions = (): UseSpotTaskActionsResult => {
    const {t} = useTranslation();
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);

    const judgeSample = useCallback(
        async (sampleId: number, result: SampleJudgeResult, remark?: string): Promise<boolean> => {
            setLoading(true);
            try {
                const res = await client.mutate<JudgeSpotSampleResponse>({
                    mutation: JUDGE_SPOT_SAMPLE,
                    variables: {input: {sampleId, judgeResult: result, judgeRemark: remark}},
                });
                return res.data?.judgePodExemptSample ?? false;
            } catch (e) {
                message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.judgeFailed'));
                return false;
            } finally {
                setLoading(false);
            }
        },
        [client],
    );

    const concludeTask = useCallback(
        async (taskId: number, conclusion: SpotTaskConclusion, remark: string): Promise<boolean> => {
            setLoading(true);
            try {
                const res = await client.mutate<ConcludeSpotTaskResponse>({
                    mutation: CONCLUDE_SPOT_TASK,
                    variables: {input: {taskId, conclusion, conclusionRemark: remark}},
                });
                const ok = res.data?.concludePodExemptSpotTask ?? false;
                if (ok) {
                    message.success(t('exemptSpotCheck.messages.concludeSuccess'));
                }
                return ok;
            } catch (e) {
                message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.concludeFailed'));
                return false;
            } finally {
                setLoading(false);
            }
        },
        [client, t],
    );

    const createTaskFromMonitor = useCallback(
        async (input: CreateSpotTaskInput): Promise<boolean> => {
            setLoading(true);
            try {
                const res = await client.mutate<CreateSpotTaskResponse>({
                    mutation: CREATE_SPOT_TASK,
                    variables: {input},
                });
                const ok = Boolean(res.data?.createPodExemptSpotTask);
                if (ok) {
                    message.success(t('exemptSpotCheck.messages.createTaskSuccess'));
                }
                return ok;
            } catch (e) {
                // errorLink 对 BUSINESS_ERROR 只记日志不弹提示，故在此翻译 messageKey 后提示，
                // 否则同周期同目标已存在这类拒绝会静默失败
                message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.createTaskFailed'));
                return false;
            } finally {
                setLoading(false);
            }
        },
        [client, t],
    );

    return {
        loading,
        judgeSample,
        concludeTask,
        createTaskFromMonitor,
    };
};
